// Route guard — oturumsuzu /giris'e yönlendirir; oturum açıksa üyelik
// yüklemesini bekler; üyelik yoksa açıklayıcı ekran gösterir.
// Supabase yapılandırılmamışsa uygulama kırılmaz, açıklayıcı ekran gösterir.

import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  Settings as SettingsIcon,
  Building2,
  LogOut,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const ProtectedRoute: React.FC = () => {
  const {
    status,
    session,
    memberships,
    companyId,
    membershipError,
    missing,
    signOut,
  } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div
        data-testid="auth-loading"
        className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground"
      >
        Oturum kontrol ediliyor…
      </div>
    );
  }

  if (status === "not_configured") {
    return (
      <div
        data-testid="auth-not-configured"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
          <SettingsIcon className="h-6 w-6 text-accent" strokeWidth={1.5} />
        </div>
        <div className="max-w-lg space-y-2">
          <h1 className="font-serif text-3xl text-foreground">
            Supabase yapılandırması bekleniyor
          </h1>
          <p className="text-sm text-muted-foreground">
            Giriş akışı için Supabase bağlantı değişkenleri henüz
            yapılandırılmadı.
          </p>
          {missing.length ? (
            <ul className="mx-auto mt-3 inline-block rounded-md border border-border bg-card px-4 py-2 text-left text-xs text-muted-foreground">
              {missing.map((m) => (
                <li key={m}>
                  <span className="font-mono">{m}</span> — eksik
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/giris" replace state={{ from: location }} />;
  }

  // Oturum var — üyelik yüklemesini bekle
  if (memberships === null) {
    return (
      <div
        data-testid="membership-loading"
        className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground"
      >
        Şirket üyeliği yükleniyor…
      </div>
    );
  }

  // Yükleme hata verdi (ör. RLS politikası reddetti)
  if (membershipError) {
    return (
      <div
        data-testid="membership-error"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle
            className="h-6 w-6 text-destructive"
            strokeWidth={1.5}
          />
        </div>
        <div className="max-w-lg space-y-2">
          <h1 className="font-serif text-3xl text-foreground">
            Üyelik bilgisi yüklenemedi
          </h1>
          <p className="text-sm text-muted-foreground">{membershipError}</p>
          <p className="text-xs text-muted-foreground">
            Bu genellikle RLS politikası kaynaklıdır. Yönetici
            <span className="mx-1 font-mono text-xs">company_members</span>
            tablosunda SELECT yetkisini kullanıcıya tanımlamalıdır.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void signOut()}
          data-testid="membership-error-signout"
          className="mt-2 gap-1.5"
        >
          <LogOut className="h-4 w-4" />
          Çıkış yap
        </Button>
      </div>
    );
  }

  // Üyelik gerçekten yok
  if (!companyId) {
    return (
      <div
        data-testid="auth-no-company"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
          <Building2 className="h-6 w-6 text-accent" strokeWidth={1.5} />
        </div>
        <div className="max-w-lg space-y-2">
          <h1 className="font-serif text-3xl text-foreground">
            Şirket üyeliği atanmamış
          </h1>
          <p className="text-sm text-muted-foreground">
            Hesabınız sisteme kayıtlı ancak henüz bir şirkete bağlı değil.
            Yönetici
            <span className="mx-1 font-mono text-xs">
              public.company_members
            </span>
            tablosuna satır eklediğinde bu ekran kaybolur.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void signOut()}
          data-testid="no-company-signout"
          className="mt-2 gap-1.5"
        >
          <LogOut className="h-4 w-4" />
          Çıkış yap
        </Button>
      </div>
    );
  }

  return <Outlet />;
};

/** Oturum açık kullanıcıyı /dashboard'a yönlendirmek için (login sayfasında). */
export const RedirectIfSignedIn: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { status, session } = useAuth();
  if (status === "ready" && session) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};
